<p-dialog
  [visible]="visible"
  (visibleChange)="onVisibleChange($event)"
  [modal]="true"
  [style]="{ width: '90rem', maxWidth: '96vw', height: '90vh' }"
  [contentStyle]="{
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: '0',
    overflow: 'hidden',
  }"
  [draggable]="false"
  [resizable]="false"
  appendTo="body"
  [header]="headerText"
>
  @if (loading) {
    <div class="flex items-center justify-center gap-2 py-12">
      <i class="pi pi-spin pi-spinner text-primary"></i>
      <span class="text-sm text-muted-color">Loading CAD geometry...</span>
    </div>
  } @else {
    @if (stepDescription) {
      <p class="mb-4 mt-0 text-sm leading-6 text-muted-color">
        {{ stepDescription }}
      </p>
    }
    <div class="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
      <div class="flex min-h-0 flex-[3_1_0%] flex-col gap-4">
        <div
          class="min-h-0 flex-[3_1_0%] overflow-hidden rounded-lg border border-surface-switch-200"
        >
          @if (cadObjects.length > 0) {
            <app-cad-viewer
              [cadObjects]="cadObjects"
              [visibleCadObjectIds]="visibleCadObjectIds"
              [highlightCadObject]="highlightCadObject"
              [externallyHoveredCadObjectId]="hoveredCadObjectId"
              (hoveredCadObjectIdChange)="hoveredCadObjectId = $event"
            />
          } @else {
            <div
              class="flex h-full items-center justify-center p-6 text-center text-sm text-muted-color"
            >
              No CAD generated for this step yet.
            </div>
          }
        </div>
        <app-cad-object-tree
          class="block min-h-0 flex-[2_1_0%]"
          [cadObjects]="cadObjectRoots"
          [primaryCadObjectId]="primaryCadObjectId"
          [cadFileId]="resultCadFileId"
          [dxfFileUrl]="nodeKind === 'add_structure' ? dxfFileUrl : null"
          [hoveredCadObjectId]="hoveredCadObjectId"
          (hoveredCadObjectIdChange)="hoveredCadObjectId = $event"
          (visibleCadObjectIdsChange)="visibleCadObjectIds = $event"
        />
      </div>
      <section
        class="flex min-h-0 flex-[2_1_0%] flex-col overflow-hidden rounded-lg border border-surface-switch-200 bg-surface-switch-0 p-4 lg:w-[30rem]"
      >
        <p-tabs value="parameters" class="flex min-h-0 flex-1 flex-col">
          <p-tablist>
            <p-tab value="parameters">Parameters</p-tab>
            <p-tab value="properties">CAD Properties</p-tab>
            <div class="ml-auto flex h-10 items-center pr-2">
              @if (editableParameters.length > 0) {
                <p-button
                  label="Reset"
                  icon="pi pi-refresh"
                  severity="secondary"
                  [text]="true"
                  styleClass="h-10"
                  (onClick)="onResetParameters()"
                />
              }
            </div>
          </p-tablist>
          <p-tabpanels class="min-h-0 flex-1 overflow-y-auto">
            <p-tabpanel value="parameters">
              @if (editableParameters.length > 0) {
                <div class="flex flex-col gap-3 pt-3">
                  @if (primaryParameters.length === 0) {
                    <p class="m-0 text-sm text-muted-color">
                      All configurable settings for this step are advanced.
                    </p>
                  }
                  @for (parameter of primaryParameters; track parameter[0]) {
                    <label
                      class="flex items-center justify-between gap-3 text-sm"
                    >
                      <span
                        class="flex items-center gap-1 text-surface-switch-700"
                      >
                        {{ formatParameterLabel(parameter[0]) }}
                        @if (parameterDescriptions[parameter[0]]) {
                          <i
                            class="pi pi-info-circle cursor-help text-primary"
                            [pTooltip]="parameterDescriptions[parameter[0]]"
                            tooltipPosition="top"
                          ></i>
                        }
                      </span>
                      @if (isBooleanParameterValue(parameter[1])) {
                        <input
                          type="checkbox"
                          [checked]="parameter[1]"
                          (change)="
                            onParameterInputChange(
                              parameter[0],
                              parameter[1],
                              $event
                            )
                          "
                          class="h-4 w-4 rounded border border-surface-switch-300"
                        />
                      } @else {
                        <div
                          class="flex w-44 shrink-0 items-center justify-end gap-2"
                        >
                          <input
                            type="number"
                            [value]="parameter[1]"
                            (change)="
                              onParameterInputChange(
                                parameter[0],
                                parameter[1],
                                $event
                              )
                            "
                            class="w-28 rounded-md border border-surface-switch-300 bg-surface-switch-100 px-3 py-2 text-right text-sm text-surface-switch-900"
                          />
                          <span
                            class="w-10 whitespace-nowrap text-right text-muted-color"
                          >
                            {{
                              formatParameterUnit(
                                processing.getStepParameterUnit(
                                  nodeKind,
                                  parameter[0]
                                )
                              )
                            }}
                          </span>
                        </div>
                      }
                    </label>
                  }
                  @if (secondaryParameters.length > 0) {
                    <section
                      class="mt-1 border-t border-surface-switch-200 pt-3"
                    >
                      <div
                        class="flex h-11 items-center justify-between gap-3 text-md font-medium text-surface-switch-700"
                      >
                        <span>Advanced Parameters</span>
                        <p-button
                          [icon]="
                            showingAllParameters
                              ? 'pi pi-chevron-up !text-xl'
                              : 'pi pi-chevron-down !text-xl'
                          "
                          severity="secondary"
                          [text]="true"
                          styleClass="h-10 w-10 !text-xl"
                          [ariaLabel]="
                            showingAllParameters
                              ? 'Hide advanced parameters'
                              : 'Show advanced parameters'
                          "
                          [attr.aria-expanded]="showingAllParameters"
                          (onClick)="toggleAllParameters()"
                        />
                      </div>
                      @if (showingAllParameters) {
                        <div class="flex flex-col gap-3 pt-3">
                          @for (
                            parameter of secondaryParameters;
                            track parameter[0]
                          ) {
                            <label
                              class="flex items-center justify-between gap-3 text-sm"
                            >
                              <span
                                class="flex items-center gap-1 text-surface-switch-700"
                              >
                                {{ formatParameterLabel(parameter[0]) }}
                                @if (parameterDescriptions[parameter[0]]) {
                                  <i
                                    class="pi pi-info-circle cursor-help text-primary"
                                    [pTooltip]="
                                      parameterDescriptions[parameter[0]]
                                    "
                                    tooltipPosition="top"
                                  ></i>
                                }
                              </span>
                              @if (isBooleanParameterValue(parameter[1])) {
                                <input
                                  type="checkbox"
                                  [checked]="parameter[1]"
                                  (change)="
                                    onParameterInputChange(
                                      parameter[0],
                                      parameter[1],
                                      $event
                                    )
                                  "
                                  class="h-4 w-4 rounded border border-surface-switch-300"
                                />
                              } @else {
                                <div
                                  class="flex w-44 shrink-0 items-center justify-end gap-2"
                                >
                                  <input
                                    type="number"
                                    [value]="parameter[1]"
                                    (change)="
                                      onParameterInputChange(
                                        parameter[0],
                                        parameter[1],
                                        $event
                                      )
                                    "
                                    class="w-28 rounded-md border border-surface-switch-300 bg-surface-switch-100 px-3 py-2 text-right text-sm text-surface-switch-900"
                                  />
                                  <span
                                    class="w-10 whitespace-nowrap text-right text-muted-color"
                                  >
                                    {{
                                      formatParameterUnit(
                                        processing.getStepParameterUnit(
                                          nodeKind,
                                          parameter[0]
                                        )
                                      )
                                    }}
                                  </span>
                                </div>
                              }
                            </label>
                          }
                        </div>
                      }
                    </section>
                  }
                </div>
              } @else {
                <p class="pt-3 text-sm text-muted-color">
                  This step has no configurable parameters.
                </p>
              }
            </p-tabpanel>
            <p-tabpanel value="properties">
              @if (displayCadObject) {
                <app-cad-properties [cadObject]="displayCadObject" />
              }
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      </section>
    </div>
  }

  <ng-template pTemplate="footer">
    <div class="flex items-center justify-between gap-2">
      <span class="text-sm text-muted-color">
        @if (status === "pending" || status === "processing") {
          <i class="pi pi-spin pi-spinner mr-1"></i>{{ statusText }}
        } @else if (status === "complete") {
          <i class="pi pi-check mr-1 text-green-600"></i>{{ statusText }}
        }
      </span>
      <div class="flex gap-2">
        @if (startErrorMessage || (status === "error" && runErrorMessage)) {
          <span class="self-center text-sm text-red-600">
            {{ startErrorMessage ?? runErrorMessage }}
          </span>
        }
        <p-button
          label="Close"
          severity="secondary"
          [outlined]="true"
          (onClick)="onClose()"
        />
        <span
          [pTooltip]="
            processing.downstreamExecutionActive()
              ? 'Downstream steps are running. Wait for the sequence to finish.'
              : undefined
          "
          tooltipPosition="top"
        >
          <p-button
            [label]="startButtonLabel"
            [loading]="
              starting || status === 'pending' || status === 'processing'
            "
            [disabled]="!canStart"
            (onClick)="onStart()"
          />
        </span>
      </div>
    </div>
  </ng-template>
</p-dialog>
